import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Typography,
} from "@mui/material";
import { useSourceOfIncomeList } from "Hooks/Options/useSourceOfIncome";
import { SourceOfIncome } from "Infrastructure/Api/Api";
import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { Resources, useResource } from "Translations/Resources";

const HINT_RESOURCES = Resources.Form.SourceOfIncomeHint;

const HINT_KEYS = new Set<SourceOfIncome>([
  SourceOfIncome.GoodsSalesRevenue,
  SourceOfIncome.ServiceAndCraftIncome,
  SourceOfIncome.HospitalityAndAccommodation,
  SourceOfIncome.CommissionsAndLicensing,
  SourceOfIncome.PropertyAndInvestmentIncome,
  SourceOfIncome.PublicBudgetsAndGrants,
  SourceOfIncome.OtherBusinessIncome,
]);

type Props<FormType extends FieldValues, TransformedFieldValues = FormType> = {
  name: FieldPath<FormType>;
  control: Control<FormType, unknown, TransformedFieldValues>;
  label?: string;
  disabled?: boolean;
};

export const IncomeSourceCheckboxGroup = <
  T extends FieldValues,
  V extends FieldValues,
>({
  control,
  name,
  label,
  disabled,
}: Props<T, V>) => {
  const { t } = useResource();
  const codeList = useSourceOfIncomeList();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const currentValues: Array<SourceOfIncome> = value ?? [];

        return (
          <FormControl component="fieldset" error={!!error} disabled={disabled}>
            {!!label && (
              <FormLabel
                component="legend"
                sx={{
                  color: "text.primary",
                  "&.Mui-focused": { color: "text.primary" },
                  fontWeight: 400,
                  fontSize: 14,
                }}
              >
                {label}
              </FormLabel>
            )}
            <FormGroup sx={{ gap: [3], mt: [3] }}>
              {codeList.map((option) => {
                const hint = HINT_KEYS.has(option.code)
                  ? t(
                      HINT_RESOURCES[
                        option.code as keyof typeof HINT_RESOURCES
                      ],
                    )
                  : undefined;

                return (
                  <FormControlLabel
                    key={option.code}
                    sx={{ alignItems: "flex-start" }}
                    control={
                      <Checkbox
                        sx={{ pt: 0 }}
                        disableRipple
                        checked={currentValues.includes(option.code)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            onChange([...currentValues, option.code]);
                          } else {
                            onChange(
                              currentValues.filter(
                                (v: SourceOfIncome) => v !== option.code,
                              ),
                            );
                          }
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" lineHeight={1}>
                          {option.name}
                        </Typography>
                        {!!hint && (
                          <Typography variant="caption" color="text.secondary">
                            {hint}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </FormGroup>
            {!!error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
};
